import { inject, Service } from '@angular/core';
import { Angulartics2 } from 'angulartics2';

/** How a consumer's foodback was opened in the All Foodbacks details drawer. */
export type FoodbackDetailsOpener = 'click' | 'keyboard' | 'tap';

/** How the global search was opened. */
export type GlobalSearchOpener = 'button' | 'shortcut';

/** What the global search held when it was closed without a pick. */
export type GlobalSearchClosedState = 'empty' | 'results' | 'no_results';

const GLOBAL_SEARCH = 'global_search';

/**
 * Product events sent to Google Analytics. Never put consumer or customer data in an event:
 * category, action and label are fixed strings. The global search's labels are built only from
 * strings in this codebase — result kinds, action ids and translation keys — never from what was
 * typed or the names of what was found, which can name a customer or a person.
 */
@Service()
export class AnalyticsEventsService {
  private angulartics = inject(Angulartics2);

  foodbackDetailsOpened(openedBy: FoodbackDetailsOpener): void {
    this.angulartics.eventTrack.next({
      action: 'open_foodback_details',
      properties: { category: 'all_foodbacks', label: openedBy },
    });
  }

  globalSearchOpened(openedBy: GlobalSearchOpener): void {
    this.angulartics.eventTrack.next({
      action: 'open_search',
      properties: { category: GLOBAL_SEARCH, label: openedBy },
    });
  }

  /**
   * `matchedBy` says what was picked and how it was found, e.g. `page_field:SHARED.SYSTEM.CITY`
   * or `recent_unit`; `position` is the row's place in the list, from 1. A pick on a tab other
   * than All is reported once more, so tab use can be counted without counting every switch.
   */
  globalSearchResultPicked(matchedBy: string, position: number, tab: string): void {
    this.angulartics.eventTrack.next({
      action: 'pick_search_result',
      properties: { category: GLOBAL_SEARCH, label: matchedBy, value: position },
    });

    if (tab !== 'all') {
      this.angulartics.eventTrack.next({
        action: 'pick_on_search_tab',
        properties: { category: GLOBAL_SEARCH, label: tab },
      });
    }
  }

  globalSearchClosedWithoutPick(state: GlobalSearchClosedState): void {
    this.angulartics.eventTrack.next({
      action: 'close_search_without_pick',
      properties: { category: GLOBAL_SEARCH, label: state },
    });
  }

  globalSearchHistoryCleared(): void {
    this.angulartics.eventTrack.next({
      action: 'clear_search_history',
      properties: { category: GLOBAL_SEARCH },
    });
  }
}
